import React, { useState } from 'react';
import { Save, X, User, Mail, Phone, Calendar, Book, AlertCircle, CreditCard } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import ImageUpload from '../ui/ImageUpload';
import toast from 'react-hot-toast';
import CepAutoFill from './CepAutoFill';

const ParentStudentForm = ({ onSubmit, onCancel, loading = false, parentData }) => {
  const [formData, setFormData] = useState({
    // Dados do aluno
    name: '',
    email: '',
    phone: '',
    registration_number: '',
    birth_date: '',
    class_name: '',
    
    // Dados dos responsáveis (vem do parentData)
    parent_name: parentData?.name || '',
    parent_email: parentData?.email || '',
    parent_phone: parentData?.phone || '',
    parent_cpf: parentData?.document || '',
    
    // Foto do aluno
    photo: null,
    photo_url: null,
    
    // Endereço
    address: '',
    city: '',
    state: '',
    zip_code: '',
    
    // Configurações da conta
    password: '',
    confirm_password: '',
    balance: '0.00',
    credit_limit: '50.00',
    active: true,
    
    // Tipo de pagamento
    payment_type: 'prepaid', // prepaid, credit, monthly
    
    // Observações
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar erro específico quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handlePhotoUpload = (file, error) => {
    if (error) {
      toast.error(error);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      photo: file,
      photo_url: file ? URL.createObjectURL(file) : null
    }));
  };

  const handleAddressChange = (addressData) => {
    setFormData(prev => ({
      ...prev,
      ...addressData
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validações obrigatórias
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.registration_number.trim()) {
      newErrors.registration_number = 'Número de matrícula é obrigatório';
    }

    if (!formData.birth_date) {
      newErrors.birth_date = 'Data de nascimento é obrigatória';
    }

    if (!formData.class_name.trim()) {
      newErrors.class_name = 'Turma é obrigatória';
    }

    // Validação de senha (opcional para estudantes)
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (formData.password && formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Senhas não coincidem';
    }

    // Validação do limite de crédito
    if (formData.credit_limit && isNaN(parseFloat(formData.credit_limit))) {
      newErrors.credit_limit = 'Limite de crédito deve ser um número válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erro ao cadastrar aluno');
    }
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Cadastrar Novo Aluno</h2>
              <p className="text-sm text-gray-600">Adicione um novo filho à sua conta</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais do Aluno */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Dados Pessoais do Aluno
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Nome Completo *"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={errors.name}
                  placeholder="Nome completo do aluno"
                />
              </div>

              <div>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <Input
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={errors.phone}
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <Input
                  label="Data de Nascimento *"
                  name="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={handleInputChange}
                  error={errors.birth_date}
                />
              </div>
            </div>
          </div>

          {/* Dados Acadêmicos */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Book className="w-5 h-5 mr-2" />
              Dados Acadêmicos
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Número de Matrícula *"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleInputChange}
                  error={errors.registration_number}
                  placeholder="Ex: 2024001"
                />
              </div>              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Turma/Série *
                </label>
                <select
                  name="class_name"
                  value={formData.class_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.class_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Selecione a turma</option>
                  <option value="Berçário">Berçário</option>
                  <option value="Maternal I">Maternal I</option>
                  <option value="Maternal II">Maternal II</option>
                  <option value="Jardim I">Jardim I</option>
                  <option value="Jardim II">Jardim II</option>
                  <option value="1º Ano">1º Ano</option>
                  <option value="2º Ano">2º Ano</option>
                  <option value="3º Ano">3º Ano</option>
                  <option value="4º Ano">4º Ano</option>
                  <option value="5º Ano">5º Ano</option>
                  <option value="6º Ano">6º Ano</option>
                  <option value="7º Ano">7º Ano</option>
                  <option value="8º Ano">8º Ano</option>
                  <option value="9º Ano">9º Ano</option>
                  <option value="1º Ensino Médio">1º Ensino Médio</option>
                  <option value="2º Ensino Médio">2º Ensino Médio</option>
                  <option value="3º Ensino Médio">3º Ensino Médio</option>
                </select>
                {errors.class_name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.class_name}
                  </p>
                )}
              </div>
            </div>
          </div>          {/* Foto do Aluno */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Foto do Aluno</h3>
            <ImageUpload
              onChange={handlePhotoUpload}
              value={formData.photo_url}
              placeholder="Clique para adicionar uma foto do aluno"
            />
          </div>

          {/* Endereço */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Endereço</h3>
            <CepAutoFill
              onAddressChange={handleAddressChange}
              initialData={{
                zip_code: formData.zip_code,
                address: formData.address,
                city: formData.city,
                state: formData.state
              }}
            />
          </div>

          {/* Configurações da Conta */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Configurações da Conta
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Pagamento
                </label>
                <select
                  name="payment_type"
                  value={formData.payment_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="prepaid">Pré-pago (recargas)</option>
                  <option value="credit">Crédito (limite)</option>
                  <option value="monthly">Mensalista</option>
                </select>
              </div>

              <div>
                <Input
                  label="Limite de Crédito (R$)"
                  name="credit_limit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.credit_limit}
                  onChange={handleInputChange}
                  error={errors.credit_limit}
                  placeholder="0.00"
                />
              </div>

              <div className="md:col-span-2">
                <Input
                  label="Senha (opcional)"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={errors.password}
                  placeholder="Deixe em branco se não quiser criar senha"
                />
              </div>

              {formData.password && (
                <div className="md:col-span-2">
                  <Input
                    label="Confirmar Senha"
                    name="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    error={errors.confirm_password}
                    placeholder="Digite a senha novamente"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Observações adicionais sobre o aluno (opcional)"
            />
          </div>

          {/* Informações do Responsável */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-md font-semibold text-blue-900 mb-2">Dados do Responsável</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-blue-700">Nome:</span>
                <span className="ml-2 font-medium text-blue-900">{formData.parent_name}</span>
              </div>
              <div>
                <span className="text-blue-700">Email:</span>
                <span className="ml-2 font-medium text-blue-900">{formData.parent_email}</span>
              </div>
              <div>
                <span className="text-blue-700">Telefone:</span>
                <span className="ml-2 font-medium text-blue-900">{formData.parent_phone || 'Não informado'}</span>
              </div>
              <div>
                <span className="text-blue-700">CPF:</span>
                <span className="ml-2 font-medium text-blue-900">{formData.parent_cpf || 'Não informado'}</span>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Cadastrando...
                </div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Cadastrar Aluno
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
};

export default ParentStudentForm;